import { companySessionStorage, companyStorageKey } from '@/utils/companyStorage'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { requestLoginCode, verifyLoginCode, demoLogin, devAutoLogin, logoutFromServer,
  rememberedRequest, rememberedSignInAvailable, endDeviceSession } from '@/api/salesRepApi'
import { setSessionToken, clearSession, getSessionToken, getDevAutoLoginEmail, isDevelopment } from '@/api/config'
import { endAuthenticationGeneration, onAuthenticationFailure, protectedActionsPaused } from '@/api/authLifecycle'
import type { AuthenticationFailure } from '@/api/authLifecycle'
import type { LoginResponse, SalesRepInfo, VerifyCodeResponse } from '@/types'

const SESSION_REP_KEY = 'salesRepApp_salesRep'
const DEVICE_KEY = 'remembered-device'
const PENDING_KEY = 'pending-device-operation'
type PendingOperation = { id: string; action: 'logout' | 'forget-device'; deviceId?: string }
type PauseReason = 'verify' | 'ended' | 'retry' | 'license' | null

export const useAuthStore = defineStore('auth', () => {
  const salesRep = ref<SalesRepInfo | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const notice = ref('')
  const remembered = ref(false)
  const rememberAvailable = ref(false)
  const pauseReason = ref<PauseReason>(null)
  const deviceId = ref<string>()
  const activeUntilUtc = ref<string>()
  const rememberedUntilUtc = ref<string>()
  const reverifyAtUtc = ref<string>()
  const licenseLimitInfo = ref<VerifyCodeResponse | null>(null)
  const canRetry = ref(false)
  let initialized = false
  let pendingForegroundRestore = false
  let currentVisitEnded = false
  let epoch = 0
  let restoration: Promise<boolean> | undefined
  let lastActivity = 0
  let updatingActivity = false
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined
  let pendingFlush: Promise<boolean> | undefined

  const isAuthenticated = computed(() => !!salesRep.value && !!getSessionToken() && !protectedActionsPaused.value)
  const repName = computed(() => salesRep.value?.name ?? '')
  const repEmail = computed(() => salesRep.value?.email ?? '')
  const isAdminRep = computed(() => salesRep.value?.isAdmin ?? false)
  const canCreatePayment = computed(() => salesRep.value?.canCreatePayment ?? false)
  const canEditAccount = computed(() => salesRep.value?.canEditAccount ?? false)
  const canCreateAccount = computed(() => salesRep.value?.canCreateAccount ?? false)

  /** Reads nonsecret device state; persistent credentials are exclusively HttpOnly cookies. */
  function readLocal<T>(key: string): T | null {
    try { return JSON.parse(localStorage.getItem(companyStorageKey(key)) || 'null') as T | null }
    catch { return null }
  }

  /** Restores tab state only after the router has resolved the company. */
  function restoreSession(): void {
    if (pauseReason.value) return
    try {
      const rep = companySessionStorage.getItem(SESSION_REP_KEY)
      salesRep.value = rep && getSessionToken() ? JSON.parse(rep) : null
      const device = readLocal<{ deviceId?: string }>(DEVICE_KEY)
      remembered.value = !!device || !!getSessionToken()?.startsWith('sr2.')
      deviceId.value = device?.deviceId
    } catch { salesRep.value = null }
  }

  /** Notifies other same-company tabs without exposing a bearer or cookie. */
  function broadcast(kind: 'ended' | 'forgotten' | 'enrolled'): void {
    localStorage.setItem(companyStorageKey('auth-event'), JSON.stringify({ kind, id: crypto.randomUUID(), deviceId: deviceId.value }))
  }

  /** Serializes cookie-changing operations across tabs; the server separately locks existing device rows. */
  async function withDeviceLock<T>(operation: () => Promise<T>): Promise<T> {
    return typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request(companyStorageKey('authentication-lock'), operation) : operation()
  }

  /** Stops local access immediately. Verification retains the mounted page and representative identity. */
  function pause(reason: PauseReason, message: string): void {
    pauseReason.value = reason
    protectedActionsPaused.value = !!reason
    error.value = message
    clearTimeout(deadlineTimer)
  }

  /** Invalidates late requests before dropping a session, without restoring it in the same visit. */
  function endVisit(message: string, forget = false): void {
    epoch++
    endAuthenticationGeneration()
    currentVisitEnded = true
    clearSession()
    if (forget) {
      localStorage.removeItem(companyStorageKey(DEVICE_KEY))
      remembered.value = false
    }
    pause('ended', message)
  }

  /** Arms a visible-page check; timers never renew either deadline or acquire a seat. */
  function armDeadline(): void {
    clearTimeout(deadlineTimer)
    const dates = [rememberedUntilUtc.value, reverifyAtUtc.value].filter(Boolean).map(value => Date.parse(value!))
    const remaining = Math.min(...dates) - Date.now()
    if (!Number.isFinite(remaining)) return
    deadlineTimer = setTimeout(() => {
      if (document.visibilityState === 'visible' && remembered.value && !currentVisitEnded)
        pause('verify', 'Please verify your email again. Your unfinished work remains on this page; save it when verification is complete.')
    }, Math.min(Math.max(remaining, 0), 2147483647))
  }

  /** Applies server deadlines and freshly checked permissions only to this visit and representative. */
  function applyResult(result: VerifyCodeResponse, requestEpoch: number): boolean {
    if (requestEpoch !== epoch) return false
    if (result.remembered) {
      const changed = deviceId.value !== result.deviceId
      remembered.value = true
      deviceId.value = result.deviceId
      localStorage.setItem(companyStorageKey(DEVICE_KEY), JSON.stringify({ deviceId: result.deviceId }))
      if (changed && result.success) broadcast('enrolled')
    }
    if (result.activeUntilUtc) activeUntilUtc.value = result.activeUntilUtc
    if (result.rememberedUntilUtc) rememberedUntilUtc.value = result.rememberedUntilUtc
    if (result.reverifyAtUtc) reverifyAtUtc.value = result.reverifyAtUtc
    if (result.licenseLimitReached) {
      licenseLimitInfo.value = result
      error.value = result.message || 'All license seats are currently in use.'
      if (salesRep.value) pauseReason.value = 'license'
      protectedActionsPaused.value = true
      return false
    }
    if (!result.success || !result.sessionToken || !result.salesRep) {
      const outcome = result.outcome
      canRetry.value = outcome === 'temporarily_unavailable' || outcome === 'allocation_uncertain'
      error.value = result.message || 'Please sign in again.'
      if (outcome === 'session_released') {
        endVisit(error.value)
        broadcast('ended')
      } else if (['verification_required', 'credential_expired', 'credential_revoked', 'device_forgotten', 'account_unavailable'].includes(outcome || '')) {
        if (salesRep.value) pause('verify', error.value)
        else clearSession()
      } else if (outcome === 'no_device' && salesRep.value && remembered.value) {
        pause('verify', 'Please verify your email again. Your unfinished work remains on this page.')
      } else if (outcome === 'no_device') {
        clearSession()
        remembered.value = false
        localStorage.removeItem(companyStorageKey(DEVICE_KEY))
        error.value = null
      } else if (outcome === 'session_inactive' && salesRep.value) {
        pause('retry', error.value)
      } else if (canRetry.value && salesRep.value) pause('retry', error.value)
      return false
    }
    if (pauseReason.value && salesRep.value && (salesRep.value.userKey !== result.salesRep.userKey || salesRep.value.salesRepKey !== result.salesRep.salesRepKey)) {
      // A different representative must never inherit the previous representative's in-memory page.
      clearSession()
      window.location.reload()
      return false
    }
    setSessionToken(result.sessionToken)
    salesRep.value = result.salesRep
    companySessionStorage.setItem(SESSION_REP_KEY, JSON.stringify(result.salesRep))
    remembered.value = result.remembered === true
    if (!remembered.value) localStorage.removeItem(companyStorageKey(DEVICE_KEY))
    currentVisitEnded = false
    pauseReason.value = null
    protectedActionsPaused.value = false
    error.value = null
    canRetry.value = false
    licenseLimitInfo.value = null
    lastActivity = Date.now()
    armDeadline()
    return true
  }

  /** Completes offline sign-out/forget before any restoration or new verified login. */
  async function flushPending(): Promise<boolean> {
    if (pendingFlush) return pendingFlush
    pendingFlush = (async () => {
      const pending = readLocal<PendingOperation>(PENDING_KEY)
      if (!pending) return true
      try {
        const releasePending = await withDeviceLock(() => endDeviceSession(pending.action, pending.deviceId))
        if (readLocal<PendingOperation>(PENDING_KEY)?.id === pending.id) localStorage.removeItem(companyStorageKey(PENDING_KEY))
        notice.value = releasePending ? 'Signed out locally. Server-side license release is pending and will be retried automatically.' : ''
        // A different tab may have queued a newer operation while the request was running.
        return !readLocal<PendingOperation>(PENDING_KEY)
      } catch {
        notice.value = 'Signed out on this browser. Server-side license release is pending. Reconnect and retry before signing in.'
        canRetry.value = true
        return false
      }
    })()
    try { return await pendingFlush } finally { pendingFlush = undefined }
  }

  /** Restores once for a foreground visit. It never replays the request that encountered expiry. */
  async function resume(newVisit = false, replaceSessionId?: number): Promise<boolean> {
    if (restoration) return restoration
    if (currentVisitEnded && !newVisit) return false
    if (newVisit) currentVisitEnded = false
    const requestEpoch = epoch
    restoration = (async () => {
      if (!await flushPending() || requestEpoch !== epoch) return false
      isLoading.value = true
      try {
        return await withDeviceLock(async () => requestEpoch === epoch
          && applyResult(await rememberedRequest('resume', replaceSessionId), requestEpoch))
      }
      catch (e) {
        if (requestEpoch !== epoch) return false
        error.value = e instanceof Error ? e.message : 'Check your connection and retry.'
        canRetry.value = true
        if (salesRep.value) pause('retry', error.value)
        return false
      } finally { isLoading.value = false }
    })()
    try { return await restoration } finally { restoration = undefined }
  }

  /** Initializes authentication once per document, before protected routes can mount. */
  async function initialize(): Promise<void> {
    if (initialized) return
    initialized = true
    restoreSession()
    rememberAvailable.value = await rememberedSignInAvailable()
    if (!await flushPending()) { clearSession(); return }
    if (getSessionToken() && !getSessionToken()!.startsWith('sr2.')) return
    if (rememberAvailable.value) {
      if (document.visibilityState === 'visible') await resume(true)
      else { pendingForegroundRestore = true; protectedActionsPaused.value = true }
    }
  }

  /** Handles a later route visit; routing to login immediately after sign-out is deliberately inert. */
  async function enterRoute(toLogin: boolean): Promise<void> {
    if (!initialized) { await initialize(); return }
    if (!toLogin && currentVisitEnded && remembered.value) await resume(true)
  }

  /** Foreground returns count as use. Hidden timers and keep-alives never call this method. */
  async function foreground(): Promise<void> {
    if (!initialized || document.visibilityState !== 'visible' || (!remembered.value && !pendingForegroundRestore) || pauseReason.value === 'verify') return
    pendingForegroundRestore = false
    await resume(true)
  }

  /** Reports actual foreground interaction at most once per five minutes. */
  async function activity(event: Event): Promise<void> {
    if (!event.isTrusted || document.visibilityState !== 'visible' || !remembered.value || !isAuthenticated.value || updatingActivity || Date.now() - lastActivity < 300000) return
    updatingActivity = true
    lastActivity = Date.now()
    const requestEpoch = epoch
    try {
      await withDeviceLock(async () => requestEpoch === epoch
        && applyResult(await rememberedRequest('activity'), requestEpoch))
    }
    catch { /* A temporary failure does not revoke access; the next interaction can retry. */ lastActivity = Date.now() - 280000 }
    finally { updatingActivity = false }
  }

  /** Pauses protected actions without treating authorization denials as sign-out. */
  function authenticationFailed(failure: AuthenticationFailure): void {
    if (currentVisitEnded) return
    if (!remembered.value) endVisit(failure.message)
    else if (failure.outcome === 'verification_required') pause('verify', failure.message)
    else if (failure.outcome === 'session_released') {
      endVisit(failure.message)
      broadcast('ended')
    } else {
      pause(remembered.value ? 'retry' : 'verify', failure.message)
    }
  }
  onAuthenticationFailure(authenticationFailed)
  window.addEventListener('storage', event => {
    if (!initialized || event.key !== companyStorageKey('auth-event') || !event.newValue) return
    try {
      const value = JSON.parse(event.newValue)
      if (value.kind === 'enrolled') {
        if (deviceId.value && deviceId.value !== value.deviceId) endVisit('A new sign-in replaced this browser session in another tab.')
        return
      }
      if (value.deviceId && deviceId.value && value.deviceId !== deviceId.value) return
      endVisit(value.kind === 'forgotten' ? 'This device was forgotten in another tab.' : 'You signed out in another tab.', value.kind === 'forgotten')
    } catch { /* Ignore malformed, non-authoritative browser notifications. */ }
  })

  /** Requests a normal email code, including the existing CAPTCHA. */
  async function sendVerificationCode(email: string, captchaToken?: string): Promise<LoginResponse> {
    isLoading.value = true
    error.value = null
    try {
      const result = await requestLoginCode(email, captchaToken)
      if (!result.success || !result.emailSent) error.value = result.message ?? 'Failed to send verification code.'
      return result
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An error occurred.'
      return { success: false, message: error.value, emailSent: false }
    } finally { isLoading.value = false }
  }

  /** Verifies once; a remembered enrollment awaiting a seat resumes using its cookie on confirmation. */
  async function verifyCode(email: string, code: string, forceLogin = false, rememberDevice = false): Promise<boolean> {
    if (forceLogin && remembered.value && licenseLimitInfo.value?.remembered) return resume(true, licenseLimitInfo.value.replaceSessionId)
    const target = forceLogin ? licenseLimitInfo.value?.replaceSessionId : undefined
    const requestEpoch = epoch
    isLoading.value = true
    error.value = null
    try {
      if (!await flushPending()) return false
      return await withDeviceLock(async () => requestEpoch === epoch
        && applyResult(await verifyLoginCode(email, code, forceLogin, rememberDevice, target), requestEpoch))
    } catch (e) {
      canRetry.value = rememberDevice || remembered.value
      error.value = e instanceof Error ? e.message : 'An error occurred.'
      return false
    } finally { isLoading.value = false }
  }

  /** Clears this visit immediately, then completes or durably queues the server operation. */
  async function endSession(forget: boolean): Promise<void> {
    if (remembered.value) {
      const pending: PendingOperation = { id: crypto.randomUUID(), action: forget ? 'forget-device' : 'logout', deviceId: deviceId.value }
      localStorage.setItem(companyStorageKey(PENDING_KEY), JSON.stringify(pending))
      broadcast(forget ? 'forgotten' : 'ended')
      endVisit(forget ? 'This browser will require a code for its next sign-in.' : 'Signed out on this browser. It remains remembered for your next visit.', forget)
      salesRep.value = null
      pauseReason.value = null
      protectedActionsPaused.value = false
      await flushPending()
    } else {
      const request = logoutFromServer()
      endVisit('Signed out.')
      salesRep.value = null
      pauseReason.value = null
      protectedActionsPaused.value = false
      await request
    }
  }

  /** Revokes other devices only after the caller presents the explicit confirmation dialog. */
  async function forgetOtherDevices(): Promise<void> {
    try {
      const pending = await withDeviceLock(() => endDeviceSession('forget-other-devices', deviceId.value))
      notice.value = pending ? 'Other devices were forgotten. Some server-side license releases are pending and will be retried.' : 'Your other devices for this company were forgotten. This browser is still signed in.'
    } catch (e) { notice.value = e instanceof Error ? e.message : 'Could not forget the other devices. Please retry.' }
  }

  /** Demo and development authentication intentionally never enroll a remembered browser. */
  async function demoSignIn(email: string, secret: string): Promise<boolean> {
    const requestEpoch = epoch
    isLoading.value = true
    try { return applyResult(await demoLogin(email, secret), requestEpoch) }
    catch (e) { error.value = e instanceof Error ? e.message : 'Demo login failed.'; return false }
    finally { isLoading.value = false }
  }

  /** Uses the existing localhost-only development endpoint without remembering the device. */
  async function tryDevAutoLogin(email?: string): Promise<boolean> {
    if (!isDevelopment() || remembered.value || currentVisitEnded) return false
    const requestEpoch = epoch
    try { return applyResult(await devAutoLogin(email?.trim() || getDevAutoLoginEmail()), requestEpoch) }
    catch { return false }
  }

  function clearError(): void { error.value = null; licenseLimitInfo.value = null }
  return { salesRep, isLoading, error, notice, licenseLimitInfo, isAuthenticated, repName, repEmail,
    isAdminRep, canCreatePayment, canEditAccount, canCreateAccount, remembered, rememberAvailable,
    pauseReason, canRetry, restoreSession, initialize, enterRoute, foreground, activity, resume,
    sendVerificationCode, verifyCode, demoSignIn, tryDevAutoLogin,
    logout: () => endSession(false), forgetDevice: () => endSession(true), forgetOtherDevices, clearError }
})
